/** Statements of account (§4.6): list, create, and the recurring run. */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { FilePlus2, FileText, Plus, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { api, ApiError, getAccessToken } from '../../lib/api';
import {
  EMPTY_LINE,
  LineItemsEditor,
  filledLines,
  linesTotal,
  type DraftLine,
} from '../purchasing/LineItemsEditor';
import { useAuth } from '../../lib/auth';
import { CustomerSearch, type PickedCustomer } from '../../components/CustomerSearch';
import { HelpButton } from '../../components/HelpButton';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
  type ChipTone,
} from '../../components/ui';

interface BillingRow {
  id: string;
  code: string;
  type: 'PROGRESS' | 'BALANCE' | 'RECURRING';
  billingPeriod: string | null;
  billingDate: string;
  dueDate: string;
  amountVatEx: string;
  vat: string;
  total: string;
  settledAmount: string;
  status: 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'VOID';
  salesOrder: { id: string; code: string; customer: { id: string; name: string } };
  _count: { collections: number };
}

interface SalesOrderOption {
  id: string;
  code: string;
  projectName: string;
  customer: { name: string };
  unbilled: string;
}

interface Billable {
  contractAmountVatEx: string;
  billedAmountVatEx: string;
  remainingVatEx: string;
  vatRate: string;
  deliverables: {
    id: string;
    title: string;
    column: string;
    amountVatEx: string;
    billed: boolean;
    completedAt: string | null;
  }[];
  /** What was sold, carried from the proposal through the lead. */
  items: {
    id: string;
    lineNo: number;
    name: string;
    description: string | null;
    unit: string | null;
    qty: string;
    amountVatEx: string;
  }[];
}

export const BILLING_TONES: Record<BillingRow['status'], ChipTone> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  PARTIALLY_PAID: 'warning',
  PAID: 'ok',
  VOID: 'neutral',
};

export const BILLING_LABELS: Record<BillingRow['status'], string> = {
  DRAFT: 'Draft',
  ISSUED: 'Issued',
  PARTIALLY_PAID: 'Partially Paid',
  PAID: 'Paid',
  VOID: 'Void',
};

async function downloadStatement(billingId: string, code: string): Promise<void> {
  const response = await fetch(`/api/v1/billings/${billingId}/pdf?inline=false`, {
    credentials: 'include',
    headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
  });
  if (!response.ok) return;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${code}.pdf`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function BillingPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  /*
   * The picked customer lives on the URL, so coming back from a statement or
   * the customer's Statement of Account keeps the list on that client.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = searchParams.get('customer');
  const customer: PickedCustomer | null = customerId
    ? { id: customerId, name: searchParams.get('customerName') ?? '' }
    : null;
  const pickCustomer = (picked: PickedCustomer | null): void => {
    const next = new URLSearchParams(searchParams);
    if (picked) {
      next.set('customer', picked.id);
      next.set('customerName', picked.name);
    } else {
      next.delete('customer');
      next.delete('customerName');
    }
    setSearchParams(next, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  /** Particulars for an itemised statement, as the proposal reads them. */
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);
  /** The sales order whose items are already in the editor. */
  const [linesFrom, setLinesFrom] = useState<string | null>(null);
  const [form, setForm] = useState({
    salesOrderId: '',
    mode: 'ITEMS' as 'PERCENT' | 'AMOUNT' | 'DELIVERABLES' | 'BALANCE' | 'ITEMS',
    percent: '30',
    amountVatEx: '',
    deliverableIds: [] as string[],
    billingDate: new Date().toISOString().slice(0, 10),
    remarks: '',
    issueNow: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const params = new URLSearchParams({ pageSize: '50' });
  if (search) params.set('q', search);
  if (status) params.set('status', status);
  if (customer) params.set('customerId', customer.id);

  const billings = useQuery({
    queryKey: ['billings', params.toString()],
    queryFn: () => api<{ data: BillingRow[]; total: number }>(`/billings?${params.toString()}`),
  });

  const orders = useQuery({
    queryKey: ['sales-orders', 'billable-options'],
    // Only orders with something left to bill, so the list stays short.
    queryFn: () =>
      api<{ data: SalesOrderOption[] }>('/sales-orders?pageSize=200&billable=true'),
    enabled: creating,
  });

  const billable = useQuery({
    queryKey: ['billable', form.salesOrderId],
    queryFn: () => api<{ data: Billable }>(`/billings/billable/${form.salesOrderId}`),
    enabled: Boolean(form.salesOrderId),
  });

  /** The sold lines, as the line editor wants them. */
  const soldLines = (billable.data?.data.items ?? []).map((item) => ({
    particular: item.name,
    unit: item.unit ?? '',
    qty: String(Number(item.qty)),
    // Stored per line, so the unit price is the line over the quantity.
    unitPrice: String(Number(item.amountVatEx) / Math.max(Number(item.qty), 1)),
  }));

  /*
   * Start an itemised statement from what was sold, rather than a blank
   * editor. Only while the editor is untouched: re-picking the same order must
   * not wipe an edit, and a statement for part of the job is made by deleting
   * the lines that are not being billed.
   */
  useEffect(() => {
    if (!form.salesOrderId) return;
    if (linesFrom === form.salesOrderId) return;
    if (soldLines.length === 0) return;
    if (filledLines(lines).length > 0 && linesFrom !== null) return;
    setLines(soldLines.map((line) => ({ ...line })));
    setLinesFrom(form.salesOrderId);
  }, [form.salesOrderId, billable.data, linesFrom, lines, soldLines]);

  const create = useMutation({
    mutationFn: () =>
      api<{ data: { id: string; code: string; total: string } }>('/billings', {
        method: 'POST',
        body: {
          salesOrderId: form.salesOrderId,
          mode: form.mode,
          percent: form.mode === 'PERCENT' ? Number(form.percent) : undefined,
          amountVatEx: form.mode === 'AMOUNT' ? Number(form.amountVatEx) : undefined,
          deliverableIds: form.mode === 'DELIVERABLES' ? form.deliverableIds : undefined,
          items: form.mode === 'ITEMS' ? filledLines(lines) : undefined,
          billingDate: form.billingDate,
          remarks: form.remarks || undefined,
          issueNow: form.issueNow,
        },
      }),
    onSuccess: (result) => {
      setError(null);
      setCreating(false);
      setLines([{ ...EMPTY_LINE }]);
      setLinesFrom(null);
      void queryClient.invalidateQueries({ queryKey: ['billings'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });

      if (form.issueNow) {
        // An issued statement exists to be sent, so download it straight away.
        setNotice(`${result.data.code} issued for ${peso(result.data.total)}. Downloading…`);
        void downloadStatement(result.data.id, result.data.code);
      } else {
        setNotice(`${result.data.code} created for ${peso(result.data.total)}.`);
      }
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create the statement.'),
  });

  const runRecurring = useMutation({
    mutationFn: () =>
      api<{ data: { created: number; skipped: number; codes: string[] } }>(
        '/jobs/recurring-billing/run',
        { method: 'POST' },
      ),
    onSuccess: (result) => {
      setError(null);
      setNotice(
        result.data.created === 0
          ? 'Nothing to bill: every recurring contract is already billed for this month.'
          : `Created ${result.data.created} recurring statement(s): ${result.data.codes.join(', ')}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['billings'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not run the recurring billing.'),
  });

  const selectedOrder = orders.data?.data.find((order) => order.id === form.salesOrderId);
  const vatRate = Number(billable.data?.data.vatRate ?? 0.12);
  const chosenDeliverables = (billable.data?.data.deliverables ?? []).filter((d) =>
    form.deliverableIds.includes(d.id),
  );
  const previewNet =
    form.mode === 'PERCENT'
      ? (Number(billable.data?.data.contractAmountVatEx ?? 0) * Number(form.percent || 0)) / 100
      : form.mode === 'AMOUNT'
        ? Number(form.amountVatEx || 0)
        : form.mode === 'BALANCE'
          ? Number(billable.data?.data.remainingVatEx ?? 0)
          : form.mode === 'ITEMS'
            ? linesTotal(lines)
            : chosenDeliverables.reduce((sum, d) => sum + Number(d.amountVatEx), 0);

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1">
            <h1 className="text-xl font-bold text-navy dark:text-white">Billing</h1>
            <HelpButton topic="billing" />
          </div>
          <p className="text-sm text-slate-500">
            Statements of account raised against sales orders. Not BIR invoices — an official
            invoice follows payment.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can('billing.create') && (
            <Button variant="secondary" busy={runRecurring.isPending} onClick={() => runRecurring.mutate()}>
              <RefreshCw aria-hidden="true" className="h-4 w-4" /> Run recurring now
            </Button>
          )}
          {can('billing.create') && (
            <Link to="/billing/manual">
              <Button variant="secondary">
                <FilePlus2 aria-hidden="true" className="h-4 w-4" /> Manual billing
              </Button>
            </Link>
          )}
          {can('billing.create') && (
            <Button onClick={() => setCreating(true)}>
              <Plus aria-hidden="true" className="h-4 w-4" /> New statement
            </Button>
          )}
        </div>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          {can('customers.view') && (
            <CustomerSearch
              className="min-w-56 flex-1"
              value={customer}
              onChange={pickCustomer}
              placeholder="Search customer name"
            />
          )}
          <div className="relative min-w-48 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder={can('customers.view') ? 'Statement or order no.' : 'Search statement, order or customer'}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <select
            aria-label="Status"
            className={`${inputClass.replace('w-full ', '')} w-full sm:w-44`}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">All statuses</option>
            {Object.entries(BILLING_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {customer && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Showing statements for{' '}
              <Link
                to={`/customers/${customer.id}`}
                className="font-semibold text-navy hover:underline dark:text-white"
              >
                {customer.name || 'this customer'}
              </Link>
              {billings.data && ` · ${billings.data.total} statement${billings.data.total === 1 ? '' : 's'}`}
            </p>
            <Link to={`/billing/customers/${customer.id}/statement`}>
              <Button size="sm">
                <FileText aria-hidden="true" className="h-4 w-4" /> Statement of account
              </Button>
            </Link>
          </div>
        )}

        {billings.isLoading && <Skeleton className="h-64 w-full" />}
        {billings.data?.data.length === 0 && (
          <EmptyState
            title={customer ? 'No statements for this customer' : 'No statements yet'}
            description={
              customer
                ? 'Nothing matches the filters. Clear the customer or the other filters to see more.'
                : 'Raise one against a sales order to start billing.'
            }
          />
        )}

        {(billings.data?.data.length ?? 0) > 0 && (
          <TableWrap>
            <thead>
              <tr>
                <Th>Statement</Th>
                <Th>Customer</Th>
                <Th>Type</Th>
                <Th>Billed</Th>
                <Th>Due</Th>
                <Th align="right">Amount</Th>
                <Th align="right">Settled</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {billings.data?.data.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <Td>
                    <Link
                      to={`/billing/${row.id}`}
                      className="font-mono font-medium text-navy hover:underline dark:text-white"
                    >
                      {row.code}
                    </Link>
                    <p className="text-xs text-slate-500">{row.salesOrder.code}</p>
                  </Td>
                  <Td>{row.salesOrder.customer.name}</Td>
                  <Td>
                    {row.type === 'RECURRING'
                      ? `Recurring ${row.billingPeriod ?? ''}`
                      : row.type === 'BALANCE'
                        ? 'Balance'
                        : 'Progress'}
                  </Td>
                  <Td>{shortDate(row.billingDate)}</Td>
                  <Td>
                    <span
                      className={
                        row.status !== 'PAID' &&
                        row.status !== 'VOID' &&
                        new Date(row.dueDate) < new Date()
                          ? 'font-semibold text-red-600'
                          : ''
                      }
                    >
                      {shortDate(row.dueDate)}
                    </span>
                  </Td>
                  <Td align="right" className="tabular">
                    {peso(row.total)}
                  </Td>
                  <Td align="right" className="tabular">
                    {peso(row.settledAmount)}
                  </Td>
                  <Td>
                    <StatusChip tone={BILLING_TONES[row.status]}>
                      {BILLING_LABELS[row.status]}
                    </StatusChip>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {creating && (
        <Modal title="New statement of account" onClose={() => setCreating(false)}>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Sales order" required>
              <select
                className={inputClass}
                required
                value={form.salesOrderId}
                onChange={(event) => {
                  // Another order's lines do not belong to this one: start again
                  // from what the newly chosen order sold.
                  if (event.target.value !== form.salesOrderId) {
                    setLines([{ ...EMPTY_LINE }]);
                    setLinesFrom(null);
                  }
                  setForm({ ...form, salesOrderId: event.target.value, deliverableIds: [] });
                }}
              >
                <option value="">Choose an order</option>
                {orders.data?.data.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.code} — {order.customer.name} ({peso(order.unbilled)} unbilled)
                  </option>
                ))}
              </select>
            </Field>

            {billable.data && (
              <div className="rounded-xl bg-slate-50 p-3 text-sm dark:bg-slate-800">
                Contract {peso(billable.data.data.contractAmountVatEx)} · billed{' '}
                {peso(billable.data.data.billedAmountVatEx)} ·{' '}
                <strong>{peso(billable.data.data.remainingVatEx)} left to bill</strong>
              </div>
            )}

            <Field label="How much to bill" required>
              <select
                className={inputClass}
                value={form.mode}
                onChange={(event) =>
                  setForm({ ...form, mode: event.target.value as typeof form.mode })
                }
              >
                <option value="ITEMS">Itemised — particulars, quantity and price</option>
                <option value="PERCENT">A percentage of the contract</option>
                <option value="AMOUNT">A fixed amount</option>
                <option value="DELIVERABLES">Completed deliverables</option>
                <option value="BALANCE">Everything still unbilled</option>
              </select>
            </Field>

            {form.mode === 'ITEMS' ? (
              <div className="space-y-2">
                <LineItemsEditor
                  lines={lines}
                  onChange={setLines}
                  vatIncluded={false}
                  label="What the customer is being billed for"
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-slate-500">
                    {soldLines.length > 0
                      ? 'Taken from what was sold on this order. Delete the lines you are not billing yet.'
                      : 'Nothing was itemised on this order, so type the lines as the proposal reads them.'}
                  </p>
                  {soldLines.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => setLines(soldLines.map((line) => ({ ...line })))}
                    >
                      <RotateCcw aria-hidden="true" className="h-4 w-4" /> Start again from the
                      order
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              soldLines.length > 0 && (
                /*
                 * The order itself, not this statement's charge. It prints on
                 * every statement for the contract, so the customer always sees
                 * what they bought whatever share is being billed now. It is
                 * read-only here because it belongs to the sales order.
                 */
                <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                  <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    What the customer ordered
                  </p>
                  <TableWrap>
                    <thead>
                      <tr>
                        <Th>Particular</Th>
                        <Th>Unit</Th>
                        <Th align="right">Qty</Th>
                        <Th align="right">Amount</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {(billable.data?.data.items ?? []).map((item) => (
                        <tr key={item.id}>
                          <Td>
                            <p>{item.name}</p>
                            {item.description && (
                              <p className="text-xs text-slate-500">{item.description}</p>
                            )}
                          </Td>
                          <Td>{item.unit ?? '—'}</Td>
                          <Td align="right">{Number(item.qty)}</Td>
                          <Td align="right">{peso(item.amountVatEx)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </TableWrap>
                  <p className="mt-2 text-xs text-slate-500">
                    Printed on every statement for this contract, whatever share is billed now.
                    Change it on the sales order.
                  </p>
                </div>
              )
            )}

            {form.mode === 'PERCENT' && (
              <Field label="Percentage" required>
                <input
                  className={`${inputClass} tabular`}
                  type="number"
                  step="0.01"
                  min={0.01}
                  max={100}
                  required
                  value={form.percent}
                  onChange={(event) => setForm({ ...form, percent: event.target.value })}
                />
              </Field>
            )}

            {form.mode === 'AMOUNT' && (
              <Field label="Amount (VAT-exclusive)" required>
                <input
                  className={`${inputClass} tabular`}
                  type="number"
                  step="0.01"
                  min={0.01}
                  required
                  value={form.amountVatEx}
                  onChange={(event) => setForm({ ...form, amountVatEx: event.target.value })}
                />
              </Field>
            )}

            {form.mode === 'DELIVERABLES' && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Deliverables</p>
                {(billable.data?.data.deliverables ?? []).map((deliverable) => (
                  <label
                    key={deliverable.id}
                    className={`flex items-center gap-2 rounded-lg border p-2 text-sm ${
                      deliverable.billed
                        ? 'border-slate-200 opacity-50 dark:border-slate-800'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                      disabled={deliverable.billed}
                      checked={form.deliverableIds.includes(deliverable.id)}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          deliverableIds: event.target.checked
                            ? [...form.deliverableIds, deliverable.id]
                            : form.deliverableIds.filter((id) => id !== deliverable.id),
                        })
                      }
                    />
                    <span className="flex-1">{deliverable.title}</span>
                    {deliverable.column === 'COMPLETED' && <StatusChip tone="ok">Done</StatusChip>}
                    {deliverable.billed && <StatusChip tone="neutral">Billed</StatusChip>}
                    <span className="tabular">{peso(deliverable.amountVatEx)}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Statement date" required>
                <input
                  className={inputClass}
                  type="date"
                  required
                  value={form.billingDate}
                  onChange={(event) => setForm({ ...form, billingDate: event.target.value })}
                />
              </Field>
              <Field label="Remarks">
                <input
                  className={inputClass}
                  value={form.remarks}
                  onChange={(event) => setForm({ ...form, remarks: event.target.value })}
                />
              </Field>
            </div>

            <div className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
              <div className="flex justify-between">
                <span>Net</span>
                <span className="tabular">{peso(previewNet)}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>VAT</span>
                <span className="tabular">{peso(previewNet * vatRate)}</span>
              </div>
              <div className="flex justify-between font-bold text-navy dark:text-white">
                <span>Total</span>
                <span className="tabular">{peso(previewNet * (1 + vatRate))}</span>
              </div>
              {selectedOrder && Number(previewNet) > Number(billable.data?.data.remainingVatEx ?? 0) && (
                <p className="mt-2 text-xs font-semibold text-red-600">
                  That is more than the contract has left to bill.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                checked={form.issueNow}
                onChange={(event) => setForm({ ...form, issueNow: event.target.checked })}
              />
              Issue it straight away (otherwise it is saved as a draft)
            </label>

            <div className="flex gap-2">
              <Button type="submit" busy={create.isPending}>
                Create statement
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
